// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║   🎙️ DONATAPP - GESTIONE VOCI                                            ║
// ║   File: src/components/VoiceManagerTab.jsx                               ║
// ║   Stile: Material Design 3 / Google Style (come resto dell'app)          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, getApiHeaders } from '../config/api';

// 🎨 Material Design 3 Colors (identici a CaregiverDashboard)
const colors = {
  primary: '#1976D2',        // Blu moderno
  secondary: '#00BCD4',      // Turchese
  success: '#4CAF50',        // Verde
  error: '#EF5350',          // Rosso
  warning: '#FF9800',        // Arancione
  background: '#F8F9FA',     // Grigio chiarissimo
  surface: '#FFFFFF',        // Bianco
  surfaceVariant: '#F5F5F5', // Grigio ultra chiaro
  outline: '#E0E0E0',        // Grigio outline
  textPrimary: '#1A1A1A',    // Quasi nero
  textSecondary: '#666666',  // Grigio scuro
  textTertiary: '#999999',   // Grigio medio
};

// 🎨 Stili riutilizzabili (identici a CaregiverDashboard)
const styles = {
  // Container principale
  container: {
    padding: 24,
    maxWidth: 1200,
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    background: colors.background,
    minHeight: '100vh',
  },
  
  // Card standard
  card: {
    background: colors.surface,
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: `1px solid ${colors.outline}`,
    marginBottom: 24,
  },
  
  // Header card
  cardHeader: {
    fontSize: 20,
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  
  // Titolo pagina
  pageTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.textPrimary,
    marginBottom: 8,
  },
  pageSubtitle: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 32,
  },
  
  // Griglia statistiche
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 32,
  },
  statCard: {
    background: colors.surface,
    borderRadius: 16,
    padding: 20,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: `1px solid ${colors.outline}`,
    textAlign: 'center',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.textPrimary,
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  
  // Griglia voci
  voicesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 20,
    marginBottom: 32,
  },
  
  // Card singola voce
  voiceCard: {
    background: colors.surface,
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: `1px solid ${colors.outline}`,
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  voiceName: {
    fontSize: 20,
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: 4,
  },
  voiceRelation: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 12,
  },
  voiceDescription: {
    fontSize: 14,
    color: colors.textTertiary,
    marginBottom: 16,
    lineHeight: 1.5,
  },
  
  // Badge stato
  badge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 12,
  },
  badgeActive: {
    background: '#E8F5E9',
    color: '#2E7D32',
  },
  badgeFallback: {
    background: colors.surfaceVariant,
    color: colors.textSecondary,
  },
  
  // Tag trigger
  triggerContainer: {
    marginTop: 12,
    marginBottom: 16,
  },
  triggerLabel: {
    fontSize: 12,
    color: colors.textTertiary,
    marginBottom: 8,
    fontWeight: 500,
  },
  triggerTag: {
    display: 'inline-block',
    background: colors.surfaceVariant,
    padding: '4px 10px',
    borderRadius: 8,
    fontSize: 12,
    color: colors.textSecondary,
    marginRight: 6,
    marginBottom: 6,
  },
  
  // Bottoni
  button: {
    padding: '12px 24px',
    borderRadius: 12,
    border: 'none',
    fontWeight: 600,
    fontSize: 15,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  buttonPrimary: {
    background: colors.primary,
    color: '#fff',
  },
  buttonSecondary: {
    background: colors.surfaceVariant,
    color: colors.textPrimary,
  },
  buttonSuccess: {
    background: colors.success,
    color: '#fff',
  },
  buttonFull: {
    width: '100%',
  },
  
  // Input
  input: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    border: `1px solid ${colors.outline}`,
    fontSize: 16,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    outline: 'none',
    marginBottom: 16,
    boxSizing: 'border-box',
  },
  
  // Risultato test
  resultBox: {
    background: colors.surfaceVariant,
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
  },
  resultRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: `1px solid ${colors.outline}`,
  },
  
  // Tabella
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 16,
  },
  tableHeader: {
    background: colors.surfaceVariant,
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: 14,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  tableCell: {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.outline}`,
    fontSize: 14,
    color: colors.textSecondary,
  },
  
  // Loading
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    color: colors.textSecondary,
  },
  
  // Flex utilities
  flexRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
};

// Info voci con emoji e colori soft
const voiceInfo = {
  maria: { emoji: '👩', bgColor: '#FFF5F5', borderColor: '#FFCDD2' },
  renzo: { emoji: '👨', bgColor: '#E3F2FD', borderColor: '#90CAF9' },
  dottore: { emoji: '🩺', bgColor: '#E8F5E9', borderColor: '#A5D6A7' },
  bella: { emoji: '🎙️', bgColor: '#FFF8E1', borderColor: '#FFE082' }
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎙️ COMPONENTE PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════════

const VoiceManagerTab = () => {
  // State
  const [voices, setVoices] = useState([]);
  const [usage, setUsage] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testText, setTestText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [simulationResult, setSimulationResult] = useState(null);

  // API_BASE_URL_URL imported from config

  // ─────────────────────────────────────────────────────────────────────────
  // CARICA DATI
  // ─────────────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Lista voci
      const voicesRes = await fetch(`${API_BASE_URL}/api/voice/multivoce/list`, { headers: getApiHeaders() });
      const voicesData = await voicesRes.json();
      if (voicesData.success) {
        setVoices(voicesData.voices);
      }

      // Statistiche utilizzo
      const usageRes = await fetch(`${API_BASE_URL}/api/voice/multivoce/usage`, { headers: getApiHeaders() });
      const usageData = await usageRes.json();
      if (usageData.success) {
        setUsage(usageData);
      }
    } catch (error) {
      console.error('Errore caricamento:', error);
    }
    setLoading(false);
  }, [API_BASE_URL]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─────────────────────────────────────────────────────────────────────────
  // TEST TUTTE LE VOCI
  // ─────────────────────────────────────────────────────────────────────────
  const testAllVoices = async () => {
    setTesting(true);
    setTestResults(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice/multivoce/test`, { headers: getApiHeaders() });
      const data = await res.json();
      setTestResults(data);
    } catch (error) {
      setTestResults({ success: false, error: error.message });
    }
    setTesting(false);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // TEST SINGOLA VOCE
  // ─────────────────────────────────────────────────────────────────────────
  const testSingleVoice = async (voiceId, voiceName) => {
    const text = `Ciao, sono ${voiceName}. Come stai oggi?`;
    setSelectedVoice(voiceId);
    setAudioUrl(null);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice/tts-multivoce`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ text, forcedVoice: voiceId })
      });
      
      const data = await res.json();
      
      if (data.success && data.audio) {
        const audioBlob = base64ToBlob(data.audio, 'audio/mpeg');
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
      }
    } catch (error) {
      console.error('Errore test:', error);
    }
    setSelectedVoice(null);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SIMULA CONVERSAZIONE
  // ─────────────────────────────────────────────────────────────────────────
  const simulateConversation = async () => {
    if (!testText.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/semantic/chat`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ userInput: testText, userId: 1 })
      });

      const data = await res.json();

      if (data.response) {
        setSimulationResult({
          userMessage: testText,
          aiResponse: data.response,
          voiceUsed: data.source || 'semantic',
          metadata: {
            classification: data.classification,
            memories: data.memories,
            latencyMs: data.latencyMs,
            stars: data.stars
          }
        });

        // Note: semantic endpoint doesn't return audio
        // Audio synthesis would need to be added separately
        setAudioUrl(null);
      }
    } catch (error) {
      console.error('Errore simulazione:', error);
    }
  };

  // Utility Base64 to Blob
  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER LOADING
  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <p>Caricamento voci...</p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPALE
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      
      {/* HEADER */}
      <h1 style={styles.pageTitle}>Gestione Voci</h1>
      <p style={styles.pageSubtitle}>
        Sistema multi-voce con voci familiari per Donata
      </p>

      {/* STATISTICHE */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>
            {usage?.characterCount?.toLocaleString() || '—'}
          </div>
          <div style={styles.statLabel}>
            Caratteri usati / {usage?.characterLimit?.toLocaleString() || '—'}
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{
            ...styles.statValue,
            color: usage?.usagePercent > 80 ? colors.warning : colors.success
          }}>
            {usage?.usagePercent || '—'}%
          </div>
          <div style={styles.statLabel}>Utilizzo mensile</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statValue}>{usage?.tier || 'N/A'}</div>
          <div style={styles.statLabel}>
            {usage?.success ? 'Connesso' : 'Non configurato'}
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statValue}>{voices.length}</div>
          <div style={styles.statLabel}>Voci disponibili</div>
        </div>
      </div>

      {/* GRIGLIA VOCI */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>Voci Familiari</div>
        
        <div style={styles.voicesGrid}>
          {voices.map((voice) => {
            const info = voiceInfo[voice.id] || voiceInfo.bella;
            return (
              <div 
                key={voice.id} 
                style={{
                  ...styles.voiceCard,
                  background: info.bgColor,
                  borderColor: info.borderColor,
                }}
              >
                <div style={styles.voiceName}>{voice.name}</div>
                <div style={styles.voiceRelation}>{voice.relation}</div>

                <span style={{
                  ...styles.badge,
                  ...(voice.id === 'caterina' ? styles.badgeFallback : styles.badgeActive)
                }}>
                  {voice.id === 'caterina' ? 'Default' : 'Attiva'}
                </span>
                
                <div style={styles.voiceDescription}>{voice.description}</div>
                
                {voice.triggerCount > 0 && (
                  <div style={styles.triggerContainer}>
                    <div style={styles.triggerLabel}>
                      Trigger ({voice.triggerCount}):
                    </div>
                    {voice.sampleTriggers?.slice(0, 4).map((trigger, i) => (
                      <span key={i} style={styles.triggerTag}>{trigger}</span>
                    ))}
                    {voice.triggerCount > 4 && (
                      <span style={styles.triggerTag}>+{voice.triggerCount - 4}</span>
                    )}
                  </div>
                )}
                
                <button
                  style={{
                    ...styles.button,
                    ...styles.buttonPrimary,
                    ...styles.buttonFull,
                  }}
                  onClick={() => testSingleVoice(voice.id, voice.name)}
                  disabled={selectedVoice === voice.id}
                >
                  {selectedVoice === voice.id ? 'Generando...' : 'Ascolta Voce'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* AUDIO PLAYER */}
      {audioUrl && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>Audio Generato</div>
          <audio
            controls
            autoPlay
            src={audioUrl}
            style={{ width: '100%', borderRadius: 8 }}
          />
        </div>
      )}

      {/* SIMULATORE */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>Simulatore Selezione Voce</div>
        <p style={{ color: colors.textSecondary, marginBottom: 16 }}>
          Scrivi una frase come la direbbe Donata e vedi quale voce risponde
        </p>
        
        <input
          type="text"
          style={styles.input}
          placeholder="Es: Dov'è Marco? Ho fame. La medicina..."
          value={testText}
          onChange={(e) => setTestText(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && simulateConversation()}
        />
        
        <div style={styles.flexRow}>
          <button
            style={{ ...styles.button, ...styles.buttonPrimary }}
            onClick={simulateConversation}
          >
            Simula Conversazione
          </button>
          <button
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onClick={testAllVoices}
            disabled={testing}
          >
            {testing ? 'Testing...' : 'Testa Tutte'}
          </button>
          <button
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onClick={loadData}
          >
            Aggiorna
          </button>
        </div>
        
        {/* Risultato simulazione */}
        {simulationResult && (
          <div style={styles.resultBox}>
            <div style={{ marginBottom: 12 }}>
              <strong>Donata:</strong> "{simulationResult.userMessage}"
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>AI:</strong> "{simulationResult.aiResponse}"
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 12,
              background: colors.surface,
              borderRadius: 8,
              border: `1px solid ${colors.outline}`
            }}>
              <div>
                <strong>Voce: {simulationResult.voiceUsed?.name}</strong>
                <div style={{ fontSize: 13, color: colors.textSecondary }}>
                  {simulationResult.voiceUsed?.relation} •
                  {simulationResult.metadata?.responseTimeMs}ms •
                  {simulationResult.metadata?.memoriesUsed} ricordi
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Risultato test tutte */}
        {testResults && (
          <div style={{ ...styles.resultBox, marginTop: 16 }}>
            <div style={{ 
              marginBottom: 12,
              fontWeight: 600,
              color: testResults.success ? colors.success : colors.error
            }}>
              {testResults.message}
            </div>
            {testResults.results?.map((result, i) => (
              <div key={i} style={styles.resultRow}>
                <span>{result.name}</span>
                <span style={{ color: result.success ? colors.success : colors.error }}>
                  {result.success
                    ? `OK (${(result.audioSize/1024).toFixed(1)} KB)`
                    : `Error: ${result.error}`
                  }
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* TACATERINA LOGICA */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>Come Funziona la Selezione</div>
        <p style={{ color: colors.textSecondary, marginBottom: 16 }}>
          Il sistema assegna punteggi in base alle parole nel messaggio
        </p>
        
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Parole Chiave</th>
              <th style={styles.tableHeader}>Voce</th>
              <th style={styles.tableHeader}>Esempio</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.tableCell}>marco, figlio, mi manchi</td>
              <td style={styles.tableCell}>Marco</td>
              <td style={styles.tableCell}>"Dov'è Marco?"</td>
            </tr>
            <tr>
              <td style={styles.tableCell}>renzo, pranzo, cena, fame</td>
              <td style={styles.tableCell}>Renzo</td>
              <td style={styles.tableCell}>"Ho fame"</td>
            </tr>
            <tr>
              <td style={styles.tableCell}>medicina, pillola, dottore</td>
              <td style={styles.tableCell}>Dottore</td>
              <td style={styles.tableCell}>"La medicina"</td>
            </tr>
            <tr>
              <td style={styles.tableCell}>umore triste/confusa</td>
              <td style={styles.tableCell}>Marco</td>
              <td style={styles.tableCell}>(automatico)</td>
            </tr>
            <tr>
              <td style={styles.tableCell}>nessun match</td>
              <td style={styles.tableCell}>Caterina</td>
              <td style={styles.tableCell}>"Che tempo fa?"</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* GUIDA SETUP */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>Come Aggiungere Voci Clonate</div>
        <ol style={{ 
          paddingLeft: 20, 
          color: colors.textSecondary, 
          lineHeight: 2,
          margin: 0 
        }}>
          <li>Vai su <strong>elevenlabs.io</strong> e registrati</li>
          <li>Clicca <strong>Voices → Add Voice → Instant Voice Cloning</strong></li>
          <li>Registra 2-3 minuti della voce (Marco, Renzo, ecc.)</li>
          <li>Carica l'audio e dai un nome</li>
          <li>Copia il <strong>Voice ID</strong></li>
          <li>Su Render aggiungi: <code style={{ 
            background: colors.surfaceVariant, 
            padding: '2px 8px', 
            borderRadius: 4 
          }}>ELEVENLABS_VOICE_MARCO=voice_id</code></li>
          <li>Rideploya e testa! 🎉</li>
        </ol>
      </div>

    </div>
  );
};

export default VoiceManagerTab;
